import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, Users, ArrowLeft, CheckCircle, 
  Share2, Bookmark, Play, ArrowRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { LeadForm } from '../components/LeadForm';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryFlags = {
  DE: '🇩🇪', NL: '🇳🇱', IT: '🇮🇹', FR: '🇫🇷', PL: '🇵🇱',
  ES: '🇪🇸', PT: '🇵🇹', BE: '🇧🇪', SE: '🇸🇪', FI: '🇫🇮'
};

const countryNames = {
  DE: 'Germany', NL: 'Netherlands', IT: 'Italy', FR: 'France', PL: 'Poland',
  ES: 'Spain', PT: 'Portugal', BE: 'Belgium', SE: 'Sweden', FI: 'Finland'
};

export const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(response.data);
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: session?.title,
        text: `Check out this session: ${session?.title}`,
        url: window.location.href,
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-16 bg-[#FFFBF8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8" />
            <div className="aspect-video bg-gray-200 rounded-2xl mb-8" />
            <div className="h-4 bg-gray-200 rounded w-full mb-2" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#002A76] mb-4">Session not found</h1>
          <Link to="/sessions">
            <Button className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full">
              <ArrowLeft className="mr-2" size={16} />
              Back to Sessions
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isFree = session.session_type === 'free_session';
  const seatsFilled = session.seats_filled || 0;
  const maxSeats = session.max_seats || 100;
  const fillPercentage = Math.min((seatsFilled / maxSeats) * 100, 100);
  const seatsLeft = maxSeats - seatsFilled;

  const highlights = [
    'Live interactive Q&A with expat advisors',
    'Real-world examples and case studies',
    'Practical tips you can apply immediately',
    'Access to session recording (for masterclasses)',
    'Certificate of participation'
  ];

  return (
    <div data-testid="session-detail-page" className="min-h-screen pt-20 pb-16 bg-[#FFFBF8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Back Link */}
        <Link 
          to="/sessions"
          className="inline-flex items-center text-gray-600 hover:text-[#002A76] mb-6 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          All Sessions
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Badge className={`${isFree ? 'badge-free' : 'badge-paid'} text-white px-3 py-1`}>
                  {isFree ? 'Free Live Session' : `Masterclass • ₹${session.price}`}
                </Badge>
                <Link to={`/country/${session.country_code}`}>
                  <Badge variant="outline" className="flex items-center gap-1 px-3 py-1">
                    <span>{countryFlags[session.country_code]}</span>
                    <span>{countryNames[session.country_code]}</span>
                  </Badge>
                </Link>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#002A76] mb-4">
                {session.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <span className="flex items-center gap-2">
                  <Calendar size={18} className="text-[#0AA06A]" />
                  {formatDate(session.date)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={18} className="text-[#0AA06A]" />
                  {session.time} • {session.duration}
                </span>
              </div>
            </div>

            {/* Thumbnail */}
            <div className="relative aspect-video bg-gradient-to-br from-[#002A76]/10 to-[#0AA06A]/10 rounded-2xl overflow-hidden mb-8">
              {session.thumbnail ? (
                <img 
                  src={session.thumbnail} 
                  alt={session.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto mb-4">
                      <Play size={32} className="text-[#002A76] ml-1" fill="currentColor" />
                    </div>
                    <span className="text-6xl">{countryFlags[session.country_code]}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-[#002A76] mb-4">About This Session</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {session.description}
              </p>
            </div>

            {/* What You'll Learn */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-[#002A76] mb-4">What You'll Get</h2>
              <ul className="space-y-3">
                {highlights.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#0AA06A] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                {/* Price */}
                <div className="text-center mb-6">
                  {isFree ? (
                    <div>
                      <span className="text-3xl font-bold text-[#0AA06A]">FREE</span>
                      <p className="text-gray-500 text-sm mt-1">No credit card required</p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-3xl font-bold text-[#002A76]">₹{session.price}</span>
                      <p className="text-gray-500 text-sm mt-1">One-time payment</p>
                    </div>
                  )}
                </div>

                {/* Seats Progress */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {seatsFilled} registered
                    </span>
                    <span className={seatsLeft < 20 ? 'text-red-500 font-medium' : ''}>
                      {seatsLeft} seats left
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full progress-bar rounded-full"
                      style={{ width: `${fillPercentage}%` }}
                    />
                  </div>
                  {seatsLeft < 20 && (
                    <p className="text-xs text-red-500 mt-2 text-center animate-pulse-soft">
                      Filling fast! Register now
                    </p>
                  )}
                </div>

                {/* CTA */}
                <Dialog open={showForm} onOpenChange={setShowForm}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="register-session-btn"
                      className="w-full bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full py-6 text-lg font-semibold btn-hover-lift"
                    >
                      {isFree ? 'Register for Free' : 'Enroll Now'}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-[#002A76]">Register for Session</DialogTitle>
                    </DialogHeader>
                    <LeadForm 
                      source="session_registration"
                      sessionId={session.id}
                      onSuccess={() => setShowForm(false)}
                      compact
                    />
                  </DialogContent>
                </Dialog>

                {/* Actions */}
                <div className="flex gap-3 mt-4">
                  <Button 
                    variant="outline"
                    data-testid="share-session-btn"
                    onClick={handleShare}
                    className="flex-1 rounded-full"
                  >
                    <Share2 size={16} className="mr-2" />
                    Share
                  </Button>
                  <Button 
                    variant="outline"
                    data-testid="save-session-btn"
                    onClick={() => toast.success('Session saved!')}
                    className="flex-1 rounded-full"
                  >
                    <Bookmark size={16} className="mr-2" />
                    Save
                  </Button>
                </div>

                {/* Host Info */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-500 mb-2">Hosted by</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#002A76] flex items-center justify-center">
                      <span className="text-white font-bold">
                        {session.host_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#002A76]">{session.host_name}</p>
                      <p className="text-sm text-gray-500">TieClad Expert</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guarantee */}
              <div className="bg-[#F7F4F0] rounded-xl p-4 mt-4 text-center">
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-[#002A76]">No spam guarantee</span> – We only send session-related information
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailPage;
